import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Plus, Edit3, Trash2, Search, Ruler, Package, Layers, Users, X, History } from "lucide-react";
import StockMovementsModal from "@/components/StockMovementsModal";

const BOX_TYPES = [
  { value: "abox", label: "A Box (Standart Amerikan)", enabled: true },
  { value: "plaka", label: "Düz Plaka", enabled: true },
  { value: "half_lid", label: "Yarım Kapak", enabled: true },
  { value: "full_lid", label: "Tam Kapak", enabled: true },
];

const UNITS = ["adet", "koli", "palet"];

// A Box formülü — Fiyat Hesapla ile aynı; ön izleme amaçlı.
// BC dalgası kalın olduğundan Levha Eni'ne +4 mm; diğer dalgalarda (E/B/C/EB) +2 mm.
// Koli çift parçaysa Boyu'ya +30 mm eklenir.
function abox(L, W, H, flute, doublePiece = false) {
  const l = Number(L) || 0, w = Number(W) || 0, h = Number(H) || 0;
  if (!l || !w || !h) return { sw: 0, sl: 0, m2: 0, eniPad: 2, flute: "" };
  const f = String(flute || "").trim().toUpperCase();
  const eniPad = f === "BC" ? 4 : 2;
  const sw = w + h + eniPad;
  const sl = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  return {
    sw: Math.round(sw * 100) / 100,
    sl: Math.round(sl * 100) / 100,
    m2: Math.round((sw * sl) / 1_000_000 * 1_000_000) / 1_000_000,
    eniPad,
    flute: f,
  };
}

// Yarım Kapak formülü — Fiyat Hesapla ile aynı.
// Levha Eni  = (W / 2) + H + 3
// Levha Boyu = ((L + W) × 2) + 30  (çift parçaysa +30 → toplam +60)
function halfLid(L, W, H, doublePiece = false) {
  const l = Number(L) || 0, w = Number(W) || 0, h = Number(H) || 0;
  if (!l || !w || !h) return { sw: 0, sl: 0, m2: 0, eniPad: 3, flute: "" };
  const sw = w / 2 + h + 3;
  const sl = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  return {
    sw: Math.round(sw * 100) / 100,
    sl: Math.round(sl * 100) / 100,
    m2: Math.round((sw * sl) / 1_000_000 * 1_000_000) / 1_000_000,
    eniPad: 3,
    flute: "",
    halfLid: true,
  };
}

// Tam Kapak formülü — Fiyat Hesapla ile aynı.
// Levha Eni  = W + H
// Levha Boyu = ((L + W) × 2) + 30  (çift parçaysa +30 → toplam +60)
function fullLid(L, W, H, doublePiece = false) {
  const l = Number(L) || 0, w = Number(W) || 0, h = Number(H) || 0;
  if (!l || !w || !h) return { sw: 0, sl: 0, m2: 0, eniPad: 0, flute: "" };
  const sw = w + h;
  const sl = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  return {
    sw: Math.round(sw * 100) / 100,
    sl: Math.round(sl * 100) / 100,
    m2: Math.round((sw * sl) / 1_000_000 * 1_000_000) / 1_000_000,
    eniPad: 0,
    flute: "",
    fullLid: true,
  };
}

const fmt = (n, d = 2) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });

const previewSku = (flute, quality, L, W, H, boxType, sw, sl) => {
  const _n = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const fl = _n(flute), qu = _n(quality);
  if (boxType === "plaka") {
    const swi = parseInt(sw, 10), sli = parseInt(sl, 10);
    if (fl && qu && swi && sli) return `PLK-${fl}-${qu}-${swi}x${sli}`;
    return "PLK-otomatik";
  }
  const Li = parseInt(L, 10), Wi = parseInt(W, 10), Hi = parseInt(H, 10);
  if (fl && qu && Li && Wi && Hi) return `${fl}-${qu}-${Li}x${Wi}x${Hi}`;
  return "STK-otomatik";
};

export default function Stocks() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [movementsFor, setMovementsFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: cs }] = await Promise.all([
        api.get("/stocks"),
        api.get("/contacts"),
      ]);
      setItems(st || []);
      setContacts(cs || []);
    } catch (e) {
      toast.error("Stoklar yüklenemedi");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const contactMap = useMemo(() => {
    const m = {};
    contacts.forEach((c) => { m[c.id] = c; });
    return m;
  }, [contacts]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLocaleLowerCase("tr");
    return items.filter((it) =>
      (it.name || "").toLocaleLowerCase("tr").includes(s) ||
      (it.sku || "").toLocaleLowerCase("tr").includes(s) ||
      (it.description || "").toLocaleLowerCase("tr").includes(s)
    );
  }, [items, q]);

  const remove = async (id) => {
    if (!window.confirm("Bu stok silinsin mi?")) return;
    try { await api.delete(`/stocks/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silinemedi: " + (e?.response?.data?.detail || "")); }
  };

  return (
    <div className="space-y-6" data-testid="stocks-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Envanter</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
            <Boxes size={28} /> Stoklar
          </h1>
          <p className="text-xs text-neutral-500 mt-1.5 max-w-xl">
            Ürettiğiniz kutu modellerini tanımlayın; ölçülerden Levha Eni/Boyu otomatik hesaplanır.
            Fiyat Hesapla ekranı ve Cariler ile ilerleyen adımlarda eşleştirilecek.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="stock-new-btn"
          className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
        >
          <Plus size={14} /> Yeni Stok
        </button>
      </header>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          data-testid="stock-search"
          placeholder="SKU, ad, açıklamada ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full border border-black/20 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
        />
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="stocks-table">
          <thead>
            <tr className="border-b-2 border-black/80 bg-neutral-50">
              <th className="text-left px-3 py-2.5 label-caps">SKU</th>
              <th className="text-left px-3 py-2.5 label-caps">Ad</th>
              <th className="text-left px-3 py-2.5 label-caps">Kutu Tipi</th>
              <th className="text-right px-3 py-2.5 label-caps">Ölçü (mm)</th>
              <th className="text-right px-3 py-2.5 label-caps">Levha (mm)</th>
              <th className="text-left px-3 py-2.5 label-caps">Karton</th>
              <th className="text-left px-3 py-2.5 label-caps">Müşteriler</th>
              <th className="text-right px-3 py-2.5 label-caps">Adet</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-12 text-neutral-500 text-sm">
                  {q ? "Arama sonucu yok." : "Henüz stok yok. Sağ üstteki 'Yeni Stok' butonundan başlayın."}
                </td>
              </tr>
            ) : filtered.map((it) => (
              <tr key={it.id} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`stock-row-${it.id}`}>
                <td className="px-3 py-2 font-mono-num text-xs font-semibold">{it.sku}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.name}</div>
                  {it.description && (
                    <div className="text-[11px] text-neutral-500 truncate max-w-xs">{it.description}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {BOX_TYPES.find((b) => b.value === it.box_type)?.label || it.box_type}
                </td>
                <td className="px-3 py-2 text-right font-mono-num">
                  {it.box_type === "plaka"
                    ? <span className="text-neutral-400">—</span>
                    : <>{it.length_mm}×{it.width_mm}×{it.height_mm}</>}
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-neutral-700">
                  {it.sheet_width_mm ? (
                    <>
                      {fmt(it.sheet_width_mm, 2)} × {fmt(it.sheet_length_mm, 2)}
                      <div className="text-[10px] text-neutral-500">{fmt(it.sheet_m2, 6)} m²</div>
                    </>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {(it.flute || it.quality || (it.grammage != null && Number(it.grammage) >= 1)) ? (
                    <div>
                      <span className="font-semibold">{it.flute || "—"}</span>
                      {it.quality && <> · {it.quality}</>}
                      {it.grammage != null && Number(it.grammage) >= 1 && <span className="text-neutral-500"> · {fmt(it.grammage, 0)} g/m²</span>}
                      {it.unit_weight_g != null && (
                        <span className="text-emerald-700 font-semibold ml-1" title="Kutu başına ağırlık">
                          · {fmt(it.unit_weight_g, 1)} g/kutu
                        </span>
                      )}
                      {it.alternative_qualities && it.alternative_qualities.length > 0 && (
                        <div className="text-[10px] text-neutral-500 mt-0.5" title={`Alt: ${it.alternative_qualities.join(", ")}`}>
                          <span className="uppercase tracking-widest">Alt:</span> {it.alternative_qualities.slice(0, 3).join(" · ")}
                          {it.alternative_qualities.length > 3 && <> +{it.alternative_qualities.length - 3}</>}
                        </div>
                      )}
                    </div>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs" data-testid={`stock-contacts-${it.id}`}>
                  {(it.contact_ids && it.contact_ids.length > 0) ? (
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {it.contact_ids.slice(0, 3).map((cid) => {
                        const c = contactMap[cid];
                        return (
                          <span key={cid} title={c?.name || cid}
                                className="inline-flex items-center bg-neutral-100 border border-black/10 px-1.5 py-0.5 text-[10px] font-medium truncate max-w-[110px]">
                            {c?.name || "—"}
                          </span>
                        );
                      })}
                      {it.contact_ids.length > 3 && (
                        <span className="inline-flex items-center bg-black text-white px-1.5 py-0.5 text-[10px] font-mono-num"
                              title={it.contact_ids.slice(3).map((cid) => contactMap[cid]?.name || "—").join(", ")}>
                          +{it.contact_ids.length - 3}
                        </span>
                      )}
                    </div>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono-num" data-testid={`stock-qty-cell-${it.id}`}>
                  {(() => {
                    const q = Number(it.qty_on_hand) || 0;
                    const m = it.min_stock;
                    const low = m != null && q <= m;
                    return (
                      <span className={low ? "text-rose-700 font-bold" : ""} title={low ? `Min ${m} ${it.unit}` : undefined}>
                        {fmt(q, 0)} <span className="text-[10px] text-neutral-500">{it.unit}</span>
                        {low && <span className="ml-1 text-[10px] uppercase tracking-widest bg-rose-100 border border-rose-400 px-1 py-0.5">Kritik</span>}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setMovementsFor(it)} className="p-1.5 hover:bg-neutral-100" title="Hareketler" data-testid={`stock-movements-${it.id}`}>
                    <History size={13} />
                  </button>
                  <button onClick={() => { setEditing(it); setOpen(true); }} className="p-1.5 hover:bg-neutral-100" title="Düzenle" data-testid={`stock-edit-${it.id}`}>
                    <Edit3 size={13} />
                  </button>
                  <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-rose-50" title="Sil" data-testid={`stock-delete-${it.id}`}>
                    <Trash2 size={13} className="text-rose-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <StockModal
          editing={editing}
          contacts={contacts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}

      {movementsFor && (
        <StockMovementsModal
          stock={movementsFor}
          contacts={contacts}
          onClose={() => setMovementsFor(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function StockModal({ editing, contacts, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    box_type: editing?.box_type || "abox",
    double_piece: !!editing?.double_piece,
    length_mm: editing?.length_mm ?? "",
    width_mm: editing?.width_mm ?? "",
    height_mm: editing?.height_mm ?? "",
    // Düz Plaka için doğrudan levha ölçüsü
    sheet_width_mm: editing?.sheet_width_mm ?? "",
    sheet_length_mm: editing?.sheet_length_mm ?? "",
    flute: editing?.flute || "",
    quality: editing?.quality || "",
    alternative_qualities: Array.isArray(editing?.alternative_qualities) ? [...editing.alternative_qualities] : [],
    grammage: editing?.grammage ?? "",
    unit: editing?.unit || "adet",
    qty_on_hand: editing?.qty_on_hand ?? "",
    min_stock: editing?.min_stock ?? "",
    description: editing?.description || "",
    sku: editing?.sku || "",
    contact_ids: Array.isArray(editing?.contact_ids) ? [...editing.contact_ids] : [],
  }));
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [manualKarton, setManualKarton] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("");  // "" = tüm tedarikçiler

  useEffect(() => {
    api.get("/pricing/karton-catalog")
      .then((r) => setCatalog(r.data || []))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, []);

  // Karton catalog key formatı: flute|quality|gsm
  const kartonKey = (form.flute || form.quality || form.grammage !== "")
    ? `${form.flute}|${form.quality}|${form.grammage === "" ? "" : form.grammage}`
    : "";
  const kartonMatch = catalog.find((k) =>
    k.flute === form.flute
    && k.quality === form.quality
    && ((k.gsm == null && form.grammage === "") || Number(k.gsm) === Number(form.grammage))
  );

  const applyKartonFromCatalog = (val) => {
    if (!val) {
      setForm((f) => ({ ...f, flute: "", quality: "", grammage: "" }));
      return;
    }
    const [flute, quality, gsm] = val.split("|");
    setForm((f) => ({ ...f, flute, quality, grammage: gsm ? Number(gsm) : "" }));
  };

  const preview = useMemo(() => {
    if (form.box_type === "abox") {
      return abox(form.length_mm, form.width_mm, form.height_mm, form.flute, form.double_piece);
    }
    if (form.box_type === "half_lid") {
      return halfLid(form.length_mm, form.width_mm, form.height_mm, form.double_piece);
    }
    if (form.box_type === "full_lid") {
      return fullLid(form.length_mm, form.width_mm, form.height_mm, form.double_piece);
    }
    if (form.box_type === "plaka") {
      const sw = Number(form.sheet_width_mm) || 0;
      const sl = Number(form.sheet_length_mm) || 0;
      if (!sw || !sl) return { sw: 0, sl: 0, m2: 0, eniPad: 0, flute: "", plaka: true };
      return {
        sw: Math.round(sw * 100) / 100,
        sl: Math.round(sl * 100) / 100,
        m2: Math.round((sw * sl) / 1_000_000 * 1_000_000) / 1_000_000,
        eniPad: 0, flute: "", plaka: true,
      };
    }
    return { sw: 0, sl: 0, m2: 0, eniPad: 2, flute: "" };
  }, [form.box_type, form.length_mm, form.width_mm, form.height_mm, form.flute, form.sheet_width_mm, form.sheet_length_mm, form.double_piece]);
  const skuPlaceholder = useMemo(
    () => previewSku(form.flute, form.quality, form.length_mm, form.width_mm, form.height_mm, form.box_type, form.sheet_width_mm, form.sheet_length_mm),
    [form.flute, form.quality, form.length_mm, form.width_mm, form.height_mm, form.box_type, form.sheet_width_mm, form.sheet_length_mm]
  );

  const save = async () => {
    const nm = form.name.trim();
    if (!nm) { toast.error("Ad zorunlu"); return; }
    const isPlaka = form.box_type === "plaka";
    const payload = {
      name: nm,
      box_type: form.box_type,
      double_piece: (form.box_type === "abox" || form.box_type === "half_lid" || form.box_type === "full_lid") ? !!form.double_piece : false,
      flute: form.flute.trim() || null,
      quality: form.quality.trim() || null,
      alternative_qualities: form.alternative_qualities,
      grammage: form.grammage === "" ? null : parseFloat(form.grammage),
      unit: form.unit,
      qty_on_hand: form.qty_on_hand === "" ? 0 : parseFloat(form.qty_on_hand),
      min_stock: form.min_stock === "" ? null : parseFloat(form.min_stock),
      description: form.description.trim() || null,
      sku: form.sku.trim() || undefined,
      contact_ids: form.contact_ids,
    };
    if (isPlaka) {
      const sw = parseFloat(form.sheet_width_mm), sl = parseFloat(form.sheet_length_mm);
      if (!(sw > 0 && sl > 0)) {
        toast.error("Düz Plaka için Levha Eni ve Levha Boyu (mm) sıfırdan büyük olmalı");
        return;
      }
      payload.sheet_width_mm = sw;
      payload.sheet_length_mm = sl;
      payload.length_mm = null;
      payload.width_mm = null;
      payload.height_mm = null;
    } else {
      const L = parseFloat(form.length_mm), W = parseFloat(form.width_mm), H = parseFloat(form.height_mm);
      if (!(L > 0 && W > 0 && H > 0)) { toast.error("Ölçüler (L, W, H) sıfırdan büyük olmalı"); return; }
      payload.length_mm = L;
      payload.width_mm = W;
      payload.height_mm = H;
    }
    try {
      if (isEdit) {
        await api.put(`/stocks/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/stocks", payload);
        toast.success("Eklendi");
      }
      onSaved();
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="stock-modal"
           className="bg-white border-2 border-black w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Package size={16} /> {isEdit ? "Stok Düzenle" : "Yeni Stok"}
          </h2>
          <button onClick={onClose} className="p-1 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Temel bilgiler */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FLabel label="Ad *" className="md:col-span-2">
              <input data-testid="stock-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="input" placeholder="Örn. 30x20x15 A Box - Turuncu" />
            </FLabel>
            <FLabel label="Kutu Tipi">
              <select data-testid="stock-box-type" value={form.box_type}
                      onChange={(e) => setForm({ ...form, box_type: e.target.value })} className="input">
                {BOX_TYPES.map((b) => (
                  <option key={b.value} value={b.value} disabled={!b.enabled}>
                    {b.label}{!b.enabled ? " — Yakında" : ""}
                  </option>
                ))}
              </select>
            </FLabel>
          </div>

          {/* Ölçüler + Levha ön izleme */}
          <div className="border-2 border-black/10 p-4 space-y-3" data-testid="stock-dim-panel">
            {form.box_type === "plaka" ? (
              <>
                <div className="label-caps flex items-center gap-1.5">
                  <Ruler size={12} /> Düz Plaka Ölçüleri (mm) — Zorunlu
                </div>
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <FLabel label="Levha Eni (mm)">
                    <input type="number" step="0.1" min="0" data-testid="stock-sheet-width"
                           value={form.sheet_width_mm}
                           onChange={(e) => setForm({ ...form, sheet_width_mm: e.target.value })}
                           className="input font-mono-num" placeholder="1330" />
                  </FLabel>
                  <FLabel label="Levha Boyu (mm)">
                    <input type="number" step="0.1" min="0" data-testid="stock-sheet-length"
                           value={form.sheet_length_mm}
                           onChange={(e) => setForm({ ...form, sheet_length_mm: e.target.value })}
                           className="input font-mono-num" placeholder="456" />
                  </FLabel>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-black/10">
                  <PreviewBox label="Adet m²" value={preview.m2 ? `${fmt(preview.m2, 6)} m²` : "—"}
                              tid="stock-preview-m2" hint="Eni × Boyu / 1M" />
                  <PreviewBox label="Levha (mm × mm)"
                              value={preview.sw && preview.sl ? `${fmt(preview.sw, 0)} × ${fmt(preview.sl, 0)}` : "—"}
                              tid="stock-preview-dims" />
                  <PreviewBox label="Kutu Ağırlığı"
                              value={(preview.m2 && Number(form.grammage) > 0)
                                ? `${fmt(preview.m2 * Number(form.grammage), 2)} g` : "—"}
                              tid="stock-preview-weight" />
                </div>
              </>
            ) : (
              <>
                <div className="label-caps flex items-center gap-1.5"><Ruler size={12} /> Ölçüler (mm) — Zorunlu</div>
                <div className="grid grid-cols-3 gap-2 md:gap-3">
                  <FLabel label="Uzunluk · L">
                    <input type="number" step="0.1" min="0" data-testid="stock-length" value={form.length_mm}
                           onChange={(e) => setForm({ ...form, length_mm: e.target.value })}
                           className="input font-mono-num" placeholder="300" />
                  </FLabel>
                  <FLabel label="En · W">
                    <input type="number" step="0.1" min="0" data-testid="stock-width" value={form.width_mm}
                           onChange={(e) => setForm({ ...form, width_mm: e.target.value })}
                           className="input font-mono-num" placeholder="200" />
                  </FLabel>
                  <FLabel label="Yükseklik · H">
                    <input type="number" step="0.1" min="0" data-testid="stock-height" value={form.height_mm}
                           onChange={(e) => setForm({ ...form, height_mm: e.target.value })}
                           className="input font-mono-num" placeholder="150" />
                  </FLabel>
                </div>
                {(form.box_type === "abox" || form.box_type === "half_lid" || form.box_type === "full_lid") && (
              <>
                <div className="pt-2 border-t border-black/10">
                  <label
                    className="flex items-center gap-2.5 cursor-pointer select-none border-2 border-black/15 hover:border-black/40 p-2.5 transition-colors"
                    data-testid="stock-double-piece-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={!!form.double_piece}
                      onChange={(e) => setForm({ ...form, double_piece: e.target.checked })}
                      className="w-4 h-4 accent-black"
                      data-testid="stock-double-piece-checkbox"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-semibold">Koli çift parça mı?</div>
                      <div className="text-[10px] text-neutral-500">İşaretlendiğinde Levha Boyu'na +30 mm eklenir.</div>
                    </div>
                    {form.double_piece && (
                      <span className="text-[10px] font-mono-num font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5">
                        +30 mm
                      </span>
                    )}
                  </label>
                </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-black/10">
                <PreviewBox
                  label="Levha Eni"
                  value={preview.sw ? `${fmt(preview.sw, 2)} mm` : "—"}
                  tid="stock-preview-sw"
                  hint={
                    form.box_type === "half_lid"
                      ? "Yarım Kapak: W/2+H+3"
                      : form.box_type === "full_lid"
                      ? "Tam Kapak: W+H"
                      : (preview.flute === "BC" ? "BC: W+H+4" : "W+H+2")
                  }
                />
                <PreviewBox
                  label="Levha Boyu"
                  value={preview.sl ? `${fmt(preview.sl, 2)} mm` : "—"}
                  tid="stock-preview-sl"
                  hint={form.double_piece ? "(L+W)×2 + 30 + 30" : "(L+W)×2 + 30"}
                />
                <PreviewBox label="Adet m²" value={preview.m2 ? `${fmt(preview.m2, 6)} m²` : "—"} tid="stock-preview-m2" />
                <PreviewBox
                  label="Kutu Ağırlığı"
                  value={(preview.m2 && Number(form.grammage) > 0)
                    ? `${fmt(preview.m2 * Number(form.grammage), 2)} g`
                    : "—"}
                  tid="stock-preview-weight"
                />
              </div>
              </>
            )}
              </>
            )}
          </div>

          {/* Karton özellikleri — fiyat listesinden çekilir */}
          <div className="border-2 border-black/10 p-4 space-y-3" data-testid="stock-karton-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="label-caps flex items-center gap-1.5"><Layers size={12} /> Karton (Fiyat Listesinden)</div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={manualKarton} onChange={(e) => setManualKarton(e.target.checked)}
                       data-testid="stock-karton-manual-toggle" className="w-3 h-3 accent-black" />
                Manuel Gir
              </label>
            </div>

            {!manualKarton ? (
              <>
                {catalogLoading ? (
                  <div className="text-xs text-neutral-500">Yükleniyor…</div>
                ) : catalog.length === 0 ? (
                  <div className="text-xs text-neutral-500 border border-dashed border-black/20 p-3">
                    Fiyat listenizde henüz karton kalemi yok. Fiyat Hesapla &gt; Fiyat Listelerini Yönet
                    ekranından bir liste ekleyin ya da <b>Manuel Gir</b> ile devam edin.
                  </div>
                ) : (() => {
                  // Her karton için "birincil tedarikçi" (backend zaten aktif+en ucuz olanı ilk sıraya alıyor)
                  const primarySupplier = (k) => (k.suppliers && k.suppliers[0]?.supplier_name) || "—";
                  // Filtre için eşsiz tedarikçi listesi (kullanım sayısıyla)
                  const supplierCounts = {};
                  for (const k of catalog) {
                    for (const s of (k.suppliers || [])) {
                      const nm = s.supplier_name || "—";
                      supplierCounts[nm] = (supplierCounts[nm] || 0) + 1;
                    }
                  }
                  const supplierList = Object.entries(supplierCounts)
                    .sort((a, b) => a[0].localeCompare(b[0], "tr"));
                  // Filtre uygulanmış + tedarikçi adına göre sıralanmış katalog
                  const filtered = supplierFilter
                    ? catalog.filter((k) => (k.suppliers || []).some((s) => s.supplier_name === supplierFilter))
                    : catalog;
                  const sorted = [...filtered].sort((a, b) => {
                    const sa = primarySupplier(a), sb = primarySupplier(b);
                    const bySup = sa.localeCompare(sb, "tr");
                    if (bySup !== 0) return bySup;
                    const byFlute = String(a.flute || "").localeCompare(String(b.flute || ""), "tr");
                    if (byFlute !== 0) return byFlute;
                    return String(a.quality || "").localeCompare(String(b.quality || ""), "tr");
                  });
                  // Tedarikçi adına göre grupla (optgroup için)
                  const groups = {};
                  for (const k of sorted) {
                    const nm = primarySupplier(k);
                    if (!groups[nm]) groups[nm] = [];
                    groups[nm].push(k);
                  }
                  return (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2">
                        <select
                          data-testid="stock-karton-supplier-filter"
                          value={supplierFilter}
                          onChange={(e) => setSupplierFilter(e.target.value)}
                          className="input bg-white"
                          title="Tedarikçiye göre filtrele"
                        >
                          <option value="">Tüm tedarikçiler ({catalog.length})</option>
                          {supplierList.map(([nm, cnt]) => (
                            <option key={nm} value={nm}>{nm} ({cnt})</option>
                          ))}
                        </select>
                        <select
                          data-testid="stock-karton-select"
                          value={kartonMatch ? `${kartonMatch.flute}|${kartonMatch.quality}|${kartonMatch.gsm ?? ""}` : ""}
                          onChange={(e) => applyKartonFromCatalog(e.target.value)}
                          className="input bg-white"
                        >
                          <option value="">— Karton seçin —</option>
                          {Object.entries(groups).map(([nm, arr]) => (
                            <optgroup key={nm} label={nm}>
                              {arr.map((k) => {
                                const key = `${k.flute}|${k.quality}|${k.gsm ?? ""}`;
                                const price = k.min_price === k.max_price
                                  ? fmt(k.min_price, 2)
                                  : `${fmt(k.min_price, 2)}–${fmt(k.max_price, 2)}`;
                                return (
                                  <option key={key} value={key}>
                                    {k.flute} · {k.quality}
                                    {k.gsm != null && Number(k.gsm) >= 1 ? ` · ${fmt(k.gsm, 0)} gr` : ""}
                                    {" — "}{price} TL/{k.unit}
                                  </option>
                                );
                              })}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                      {supplierFilter && (
                        <div className="text-[10px] text-neutral-500 font-mono-num">
                          {sorted.length} kombinasyon · <b>{supplierFilter}</b> tedarikçisine göre filtrelendi
                          <button
                            type="button"
                            data-testid="stock-karton-supplier-clear"
                            onClick={() => setSupplierFilter("")}
                            className="ml-2 underline hover:text-black"
                          >
                            filtreyi temizle
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Salt-okunur özet — seçim yansıması */}
                {(form.flute || form.quality || (form.grammage !== "" && Number(form.grammage) > 0)) && (
                  <div className="grid grid-cols-3 gap-2 md:gap-3 pt-1 text-xs">
                    <ReadOnlyBox label="Flute" value={form.flute || "—"} />
                    <ReadOnlyBox label="Kalite" value={form.quality || "—"} />
                    <ReadOnlyBox label="Gramaj" value={(form.grammage !== "" && Number(form.grammage) > 0) ? `${form.grammage} g/m²` : "—"} />
                  </div>
                )}

                {/* Alternatif kaliteler — aynı flute içinde katalogtan eklenebilir */}
                {form.flute && (
                  <AlternativeQualitiesPicker
                    catalog={catalog}
                    flute={form.flute}
                    mainQuality={form.quality}
                    selected={form.alternative_qualities}
                    onChange={(alts) => setForm({ ...form, alternative_qualities: alts })}
                  />
                )}
              </>
            ) : null}
            {manualKarton && (
              <div className="grid grid-cols-3 gap-2 md:gap-3">
                <FLabel label="Flute">
                  <input data-testid="stock-flute" value={form.flute}
                         onChange={(e) => setForm({ ...form, flute: e.target.value })}
                         className="input" placeholder="B, C, BC, E…" />
                </FLabel>
                <FLabel label="Kalite">
                  <input data-testid="stock-quality" value={form.quality}
                         onChange={(e) => setForm({ ...form, quality: e.target.value })}
                         className="input" placeholder="TF, TK, KK…" />
                </FLabel>
                <FLabel label="Gramaj (g/m²)">
                  <input type="number" step="1" min="0" data-testid="stock-grammage" value={form.grammage}
                         onChange={(e) => setForm({ ...form, grammage: e.target.value })}
                         className="input font-mono-num" />
                </FLabel>
              </div>
            )}
            {/* Manuel modda da alternatif kaliteler */}
            {manualKarton && form.flute && (
              <AlternativeQualitiesPicker
                catalog={catalog}
                flute={form.flute}
                mainQuality={form.quality}
                selected={form.alternative_qualities}
                onChange={(alts) => setForm({ ...form, alternative_qualities: alts })}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FLabel label="Birim">
              <select data-testid="stock-unit" value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })} className="input">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </FLabel>
            <FLabel label="Mevcut Adet">
              <input type="number" step="1" min="0" data-testid="stock-qty" value={form.qty_on_hand}
                     onChange={(e) => setForm({ ...form, qty_on_hand: e.target.value })}
                     className="input font-mono-num" placeholder="0" />
            </FLabel>
            <FLabel label="Min Stok (uyarı)">
              <input type="number" step="1" min="0" data-testid="stock-min" value={form.min_stock}
                     onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                     className="input font-mono-num" placeholder="boş = kapalı" />
            </FLabel>
            <FLabel label={`Kod (SKU) — Otomatik: ${skuPlaceholder}`}>
              <div className="flex items-center gap-2">
                <input data-testid="stock-sku" value={form.sku}
                       onChange={(e) => setForm({ ...form, sku: e.target.value })}
                       className="input font-mono-num flex-1" placeholder={skuPlaceholder} />
                {isEdit && (
                  <button
                    type="button"
                    data-testid="regen-sku-btn"
                    title={`Otomatik kurala göre yeniden üret (Düz Plaka: PLK-FLUTE-KALİTE-EnxBoy)`}
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/stocks/${editing.id}/regenerate-sku`);
                        setForm((f) => ({ ...f, sku: data.sku }));
                        toast.success(`SKU yenilendi: ${data.sku}`);
                      } catch (e) {
                        toast.error("Yenilenemedi: " + (e?.response?.data?.detail || e?.message));
                      }
                    }}
                    className="text-[10px] uppercase tracking-widest border border-black/30 px-2 py-2 hover:bg-neutral-100 whitespace-nowrap"
                  >
                    Otomatik Üret
                  </button>
                )}
              </div>
            </FLabel>
          </div>

          <FLabel label="Açıklama">
            <textarea data-testid="stock-description" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={2} className="input" />
          </FLabel>

          {/* Cari eşleştirmesi */}
          <ContactMultiPicker
            contacts={contacts}
            selectedIds={form.contact_ids}
            onChange={(ids) => setForm({ ...form, contact_ids: ids })}
          />
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-black/10 bg-neutral-50">
          <button onClick={onClose} data-testid="stock-cancel" className="border border-black/30 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white">İptal</button>
          <button onClick={save} data-testid="stock-save" className="brutal-btn bg-black text-white px-5 py-2 text-xs uppercase tracking-widest">
            {isEdit ? "Güncelle" : "Kaydet"}
          </button>
        </div>

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.7rem;outline:none;font-size:0.85rem;background:white}
                    .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
      </div>
    </div>
  );
}

function FLabel({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <div className="label-caps mb-1.5 text-[10px]">{label}</div>
      {children}
    </label>
  );
}

function PreviewBox({ label, value, tid, hint }) {
  return (
    <div className="border border-black/10 p-2.5 bg-neutral-50" data-testid={tid}>
      <div className="label-caps text-[10px] text-neutral-500 flex items-center justify-between gap-1">
        <span>{label}</span>
        {hint && (
          <span className={`text-[9px] font-mono-num px-1 py-0.5 border ${
            hint.startsWith("BC")
              ? "bg-amber-100 border-amber-400 text-amber-800"
              : "bg-neutral-100 border-neutral-300 text-neutral-600"
          }`}>{hint}</span>
        )}
      </div>
      <div className="font-mono-num text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function ReadOnlyBox({ label, value }) {
  return (
    <div className="border border-black/10 p-2 bg-neutral-50">
      <div className="label-caps text-[10px] text-neutral-500">{label}</div>
      <div className="font-mono-num text-xs font-semibold mt-0.5">{value}</div>
    </div>
  );
}

/** Alternatif kalite seçici — aynı flute içinde katalogtan eklenebilir + serbest ekleme */
function AlternativeQualitiesPicker({ catalog, flute, mainQuality, selected, onChange }) {
  const [manualInput, setManualInput] = useState("");
  // Aynı flute için katalogdaki tüm eşsiz kaliteler (ana kalite hariç, halihazırda seçilmemiş)
  const suggestions = useMemo(() => {
    const fl = (flute || "").toUpperCase();
    const main = (mainQuality || "").toUpperCase();
    const has = new Set((selected || []).map((s) => s.toUpperCase()));
    const seen = new Set();
    const out = [];
    catalog.forEach((k) => {
      if ((k.flute || "").toUpperCase() !== fl) return;
      const q = (k.quality || "").trim();
      if (!q) return;
      const key = q.toUpperCase();
      if (key === main || has.has(key) || seen.has(key)) return;
      seen.add(key);
      out.push(q);
    });
    return out;
  }, [catalog, flute, mainQuality, selected]);

  const add = (q) => {
    const s = String(q || "").trim();
    if (!s) return;
    if (s === mainQuality) { toast.error("Ana kalite zaten seçili"); return; }
    if ((selected || []).some((x) => x.toUpperCase() === s.toUpperCase())) { toast.error("Bu kalite zaten eklenmiş"); return; }
    onChange([...(selected || []), s]);
  };
  const remove = (q) => onChange((selected || []).filter((x) => x !== q));

  return (
    <div className="border border-black/10 p-3 bg-neutral-50 space-y-2" data-testid="alt-quality-panel">
      <div className="label-caps text-[10px] flex items-center gap-1.5">
        Alternatif Kaliteler
        <span className="normal-case tracking-normal text-neutral-500">
          (aynı flute · Fiyat Hesapla otomatik seçimde ana yoksa denenir · {selected?.length || 0} seçili)
        </span>
      </div>
      {selected && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="alt-quality-chips">
          {selected.map((q) => (
            <span key={q} className="inline-flex items-center gap-1 bg-white border border-black px-2 py-0.5 text-[11px] font-mono-num">
              {q}
              <button type="button" onClick={() => remove(q)} data-testid={`alt-quality-remove-${q}`}
                      className="hover:bg-rose-50 text-rose-600" title="Kaldır">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions.slice(0, 12).map((q) => (
            <button type="button" key={q} onClick={() => add(q)}
                    data-testid={`alt-quality-add-${q}`}
                    className="text-[10px] font-mono-num border border-dashed border-black/30 px-1.5 py-0.5 hover:bg-white">
              + {q}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input
          data-testid="alt-quality-manual"
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(manualInput); setManualInput(""); } }}
          placeholder="Yeni kalite ekle (Enter)"
          className="border border-black/20 px-2 py-1 text-xs font-mono-num flex-1"
        />
        <button type="button" onClick={() => { add(manualInput); setManualInput(""); }}
                data-testid="alt-quality-manual-add"
                className="border border-black/30 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-white">
          Ekle
        </button>
      </div>
    </div>
  );
}

/** Çoklu cari seçici — search + chip list */
function ContactMultiPicker({ contacts, selectedIds, onChange }) {
  const [query, setQuery] = useState("");
  const selectedMap = useMemo(() => Object.fromEntries((selectedIds || []).map((id) => [id, true])), [selectedIds]);
  const filtered = useMemo(() => {
    if (!query.trim()) return contacts.filter((c) => !selectedMap[c.id]).slice(0, 6);
    const s = query.toLocaleLowerCase("tr");
    return contacts.filter((c) =>
      !selectedMap[c.id] && (c.name || "").toLocaleLowerCase("tr").includes(s)
    ).slice(0, 12);
  }, [contacts, query, selectedMap]);

  const add = (cid) => {
    if (selectedMap[cid]) return;
    onChange([...(selectedIds || []), cid]);
    setQuery("");
  };
  const remove = (cid) => onChange((selectedIds || []).filter((x) => x !== cid));

  return (
    <div className="border-2 border-black/10 p-4 space-y-3" data-testid="stock-contacts-panel">
      <div className="label-caps flex items-center gap-1.5">
        <Users size={12} /> Bu Ürünü Şu Müşterilere Üretiyorum
        <span className="text-[10px] normal-case tracking-normal text-neutral-500">
          ({selectedIds?.length || 0} seçili)
        </span>
      </div>

      {selectedIds && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="stock-selected-contacts">
          {selectedIds.map((cid) => {
            const c = contacts.find((x) => x.id === cid);
            return (
              <span key={cid} className="inline-flex items-center gap-1 bg-black text-white px-2 py-1 text-[11px]">
                {c?.name || "—"}
                <button onClick={() => remove(cid)} data-testid={`stock-contact-remove-${cid}`}
                        className="hover:bg-white/20 rounded-sm ml-0.5" title="Kaldır">
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          data-testid="stock-contact-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cari ara ve ekle…"
          className="input pl-8"
        />
      </div>

      {(query || filtered.length > 0) && (
        <div className="border border-black/10 divide-y divide-black/5 max-h-40 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-xs text-neutral-500 p-2">Eşleşen cari yok.</div>
          ) : filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => add(c.id)}
              data-testid={`stock-contact-add-${c.id}`}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-neutral-50 flex items-center justify-between"
            >
              <span>{c.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                {c.type === "customer" ? "Müşteri" : c.type === "supplier" ? "Tedarikçi" : c.type || ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
